/**
 * Which Release Center tone a receipt should sound, and whether it is still
 * worth sounding (#2041).
 *
 * This is the pure half of the sound path: it takes a receipt's timestamp plus
 * what this client has already sounded, and returns a decision. The impure half
 * (the audio engine, the coalescing timer) lives in WebSocketWatcher +
 * renderer/utils/play.ts.
 *
 * What it replaces: a flat `now - createdAt > 30_000 -> return`. Reconnect
 * catch-up runs through the same handler as a live event, so a WebSocket blip at
 * GO time (iOS backgrounding, tab throttling, a network hiccup) delivered the GO
 * more than 30s after it was posted and it played nothing at all. That is the
 * reported "heard Set up, never heard GO": Set up arrived live, GO arrived late.
 *
 * The rules that replace it, in order:
 *
 *  1. A receipt this client already sounded never sounds twice.
 *  2. A receipt older than `TONE_WINDOW_MS` gets the banner only — a tone for
 *     something that happened five minutes ago is worse than silence.
 *  3. A receipt older than the newest receipt the client has already CONSIDERED
 *     never sounds. This is the "a stale GO must never play over a newer
 *     message" guarantee, and it holds regardless of the order a catch-up batch
 *     arrives in, because the watermark advances on every receipt the caller
 *     processes — whether or not that receipt itself made a sound.
 *  4. Inside the window and newer than the watermark: sound it. Receipts that
 *     arrived promptly play immediately; the rest take the late path, which
 *     coalesces a burst down to the single newest tone (see
 *     `supersedesPendingTone`) and marks the banner as delivered late.
 */

/** The tone a staff message maps to. `message` is the generic new-message chime. */
export type ToneName = 'go' | 'cancel' | 'opposite' | 'setup' | 'message';

/**
 * Tones worth hearing after the fact. A GO / CANCEL / OPPOSITE is an
 * instruction that is still actionable a minute later, so a missed one sounds on
 * reconnect. A Set up or a plain message is context: it gets the banner, and
 * staying silent avoids a reconnect replaying a chime for something the user can
 * simply read on screen.
 */
export const LATE_ELIGIBLE_TONES: readonly ToneName[] = ['go', 'cancel', 'opposite'];

/** Beyond this age a receipt is banner-only, never a tone. */
export const TONE_WINDOW_MS = 3 * 60 * 1000;

/** At or under this age a receipt counts as having arrived live. */
export const TONE_LIVE_MS = 10 * 1000;

/**
 * How long the late path waits before sounding. A reconnect catch-up delivers a
 * batch of receipts within a few ticks of each other and in no guaranteed order,
 * so the late slot collects the batch for this long and sounds only its newest
 * member — one tone, not a pile-up.
 */
export const LATE_TONE_COALESCE_MS = 400;

// Message-content patterns for the three resolution tones and the set-up tone.
//
// Deliberately stricter than the `setupConfirm.ts` parser that drives the
// Create Release prefill: that one also accepts "official", "no play", "off",
// "pass" and friends, which is right for reading a feed but wrong for firing a
// klaxon — a message that merely opens with "Off" must not sound like a CANCEL.
// These keep the exact shapes the sound path has always matched.
const GO_RE = /^\W*GO\W*[.!]*$/i;
const CANCEL_RE = /^\W*CANCEL\W*[.!]*$/i;
const OPPOSITE_RE = /^\W*OPPOSITE\W*[.!]*$/i;
// "Set up 309140 Gardner Webb -6.5", "Set up: Seattle -2.5", or a bare "Set up".
const SETUP_RE = /^\W*Set up(\W|$)/i;

/**
 * The tone a receipt's message maps to. Only staff/admin messages get a
 * classified tone; anything else is the generic chime.
 */
export function classifyTone(content: string | null | undefined, isFromStaff: boolean): ToneName {
  if (!isFromStaff || !content) {
    return 'message';
  }
  if (GO_RE.test(content)) {
    return 'go';
  }
  if (CANCEL_RE.test(content)) {
    return 'cancel';
  }
  if (OPPOSITE_RE.test(content)) {
    return 'opposite';
  }
  if (SETUP_RE.test(content)) {
    return 'setup';
  }
  return 'message';
}

export type ToneOutcome =
  /** Arrived promptly — sound it now. */
  | 'play-live'
  /** Arrived late but still actionable — sound it through the late slot. */
  | 'play-late'
  /** This client already sounded this receipt. */
  | 'skip-duplicate'
  /** Older than the bounded window; banner only. */
  | 'skip-outside-window'
  /** Older than a receipt this client has already considered. */
  | 'skip-superseded'
  /** Late, but not a tone worth hearing after the fact. */
  | 'skip-late-ineligible';

export interface ToneDecision {
  outcome: ToneOutcome;
  tone: ToneName;
  /** How far behind its `createdAt` this receipt was handled, in ms. */
  ageMs: number;
}

export interface ToneDecisionInput {
  tone: ToneName;
  /** The receipt's `createdAt` in ms, or null/undefined when it has none. */
  createdAtMs?: number | null;
  now: number;
  /** Has this client already sounded this exact receipt? */
  alreadySounded: boolean;
  /**
   * `createdAt` of the newest receipt this client has already CONSIDERED for a
   * tone — not just the ones it sounded. Advancing it on every processed receipt
   * is what keeps an older GO from sounding after a newer message has landed,
   * whatever order the two arrive in.
   */
  watermarkMs: number;
  windowMs?: number;
  liveMs?: number;
}

export function decideTone({
  tone,
  createdAtMs,
  now,
  alreadySounded,
  watermarkMs,
  windowMs = TONE_WINDOW_MS,
  liveMs = TONE_LIVE_MS,
}: ToneDecisionInput): ToneDecision {
  if (alreadySounded) {
    return { outcome: 'skip-duplicate', tone, ageMs: 0 };
  }

  const createdAt =
    typeof createdAtMs === 'number' && Number.isFinite(createdAtMs) ? createdAtMs : null;

  if (createdAt === null) {
    // Nothing to judge staleness on. Treat it as live, which is what the old
    // 30s check did too (it only ran when `createdAt` was present).
    return { outcome: 'play-live', tone, ageMs: 0 };
  }

  // A receipt whose clock runs ahead of ours is not "from the future", it is
  // fresh — clamp rather than letting skew read as a negative age.
  const ageMs = Math.max(0, now - createdAt);

  if (ageMs > windowMs) {
    return { outcome: 'skip-outside-window', tone, ageMs };
  }

  if (createdAt < watermarkMs) {
    return { outcome: 'skip-superseded', tone, ageMs };
  }

  if (ageMs <= liveMs) {
    return { outcome: 'play-live', tone, ageMs };
  }

  return {
    outcome: LATE_ELIGIBLE_TONES.includes(tone) ? 'play-late' : 'skip-late-ineligible',
    tone,
    ageMs,
  };
}

/**
 * Does `createdAtMs` win the single late-tone slot over what is already pending?
 *
 * The newest receipt always wins, whatever tone it carries — that is the same
 * "newer message beats an older instruction" rule as rule 3, applied inside the
 * coalescing window. A newer receipt that is itself banner-only therefore
 * CLEARS a pending older tone rather than letting it sound behind it. Ties go to
 * the later arrival, which is harmless: both are the same age.
 */
export function supersedesPendingTone(
  pendingCreatedAtMs: number | undefined,
  createdAtMs: number,
): boolean {
  return pendingCreatedAtMs === undefined || createdAtMs >= pendingCreatedAtMs;
}

/**
 * Short human lateness for the "delivered late" banner marker: "42s", "2m".
 */
export function formatLateness(ageMs: number): string {
  const seconds = Math.max(0, Math.round(ageMs / 1000));

  if (seconds < 60) {
    return `${seconds}s`;
  }

  return `${Math.round(seconds / 60)}m`;
}
